import {
	AGREEMENT_INTERACTION_TYPES,
	AGREEMENT_REQUIREMENT_TYPES,
	VALID_INTERACTIONS_FOR_REQUIREMENT
} from "../constants";

import {
	ACCOUNT_NOTICE_READ, 
	ACCOUNT_CAMPAIGN_INTERACT
} from "../routes";

const mapCampaignToNotice = (campaign, version, subjectInteractions) => {
	const readInteraction = subjectInteractions?.find(i => VALID_INTERACTIONS_FOR_REQUIREMENT[i.Requirement.Code][i.Interaction.Code] === true);

	const mappedNotice = {
		Id: version.Id,
		Title: version.Contents[0].Title,
		Content: version.Contents[0].Content, 
		BeginInclusive: version.BeginInclusive,
		EndExclusive: version.EndExclusive,
		Interval: version.Periodicity,
		Severity: {
			Code: campaign.Purpose.Code ?? "INFO",
			Name: campaign.Purpose.Name ?? "Info",
			Description: campaign.Purpose.Description ?? "Informational notice"
		},
		Read: readInteraction?.Occurred
	};

	return mappedNotice;
};

function NoticeService(
	api,
	$logger,
	campaignService
) {
	const service = this;
	service.notices = [];
	service.hasUnreadNotices = false;

	service.isNoticeValid = (notice) => {
		const now = new Date();
		const noticeBegin = notice.BeginInclusive !== undefined ? new Date(notice.BeginInclusive) : undefined;
		const noticeEnd = notice.EndExclusive !== undefined ? new Date(notice.EndExclusive) : undefined;
		
		const isNoticeStarted = noticeBegin === undefined || noticeBegin <= now;
		const isNoticeExpired = noticeEnd !== undefined && noticeEnd < now;

		return isNoticeStarted && !isNoticeExpired;
	};

	service.showNoticeUnread = (notice) => {
		if(notice.Read === undefined) {
			return true;
		}

		if(notice.Interval === undefined) {
			return false;
		}
		
		const now = new Date();
		const lastRead = new Date(notice.Read);
		const [hours, minutes, seconds] = notice.Interval.split(":").map(part => parseInt(part, 10));
		const totalDurationInSeconds = seconds + (minutes *60) + (hours*60*60);

		const resetAt = new Date(lastRead.getTime() + (totalDurationInSeconds * 1000));

		return resetAt < now; 
	};

	service.setNotices = (campaigns) => {
		service.notices = ([...campaigns])
			.filter(c => c.IsActive === true)
			.filter(c => c.Purpose.Function.Code === "NOTICE")
			.sort((a, b) => (b.Id - a.Id))
			.map(campaign => {
				const currentVersion = campaignService.getCurrentLatestVersionForCampaign(campaign);
				const interactionsForVersion = campaignService.subjectInteractionsByVersionId[currentVersion.Id];

				const notices = mapCampaignToNotice(campaign, currentVersion, interactionsForVersion);

				return notices;
			});

		service.hasUnreadNotices = service.notices.some(n => {
			return service.showNoticeUnread(n);
		});

		return service.notices;
	};

	service.readNotice = (campaignVersionId) => {
		const campaignId = campaignService.versionIdToCampaignMap[campaignVersionId];
		if(!campaignId) {
			$logger.error("Could not find campaign for version id", { campaignVersionId, campaigns: service.campaigns });
			return Promise.reject(new Error("Notice not found"));
		}

		const readPayload = {
			Requirement: { Code: AGREEMENT_REQUIREMENT_TYPES.SHOW },
			Interaction: { Code: AGREEMENT_INTERACTION_TYPES.READ }
		};

		return api.post(ACCOUNT_CAMPAIGN_INTERACT(campaignId, campaignVersionId), readPayload)
			.then((resp) => {
				const noticeIndex = service.notices.findIndex(n => n.Id === campaignVersionId);

				service.notices = [...service.notices];
				service.notices[noticeIndex] = {
					...service.notices[noticeIndex], 
					Read: resp.Occurred
				};
				service.hasUnreadNotices = service.notices.some(n => n.Read === undefined);
			});
	};

	service.readAllNotices = () => {
		const unreadNotices = service.notices.filter(n => n.Read === undefined);

		const allRead = unreadNotices.map(n => {
			return api.post(ACCOUNT_NOTICE_READ(n.Id));
		});

		return Promise.all(allRead).then(allResp => {
			service.notices = [...service.notices];
			allResp.forEach(r => {
				const thisNotice = service.notices.find(n => n.Id === r.Notice.Id);
				thisNotice.Read = r.Read;
			});
			service.hasUnreadNotices = false;
		});
	};

	campaignService.addOnCampaignsChanged(service.setNotices);

	return service;
}

export default angular
	.module("vesl.services.notice-service", [])
	.service("noticeService", NoticeService).name;